import { ServerConnection, type ConnectionState } from "./connection";
import type { SavedProfile } from "../storage/storage";
import { nameForJoin } from "../storage/storage";

export type OnlineClientStatus = "idle" | "connecting" | "connected" | "closed" | "error";

export type OnlineServerTarget = {
  wsUrl: string;
  healthUrl?: string;
  regionId?: string;
  regionName?: string;
  priority?: number;
};

export type OnlineClientOptions = {
  targets?: OnlineServerTarget[];
  localWsUrl?: string;
  forceLocalServer?: boolean;
  connectionTimeoutMs?: number;
  pingTimeoutMs?: number;
  onStatusChange?: (status: OnlineClientStatus) => void;
  onMessage?: (message: unknown) => void;
  onError?: (error: Error) => void;
};

export type OnlineConnectResult = {
  wsUrl: string;
  healthUrl?: string;
  regionId?: string;
  regionName?: string;
  healthMs?: number;
};

type MeasuredTarget = OnlineConnectResult & {
  priority: number;
  reachable: boolean;
};

const DEFAULT_CONNECTION_TIMEOUT_MS = 6000;
const DEFAULT_PING_TIMEOUT_MS = 2500;

export class OnlineClient {
  private connection: ServerConnection | null = null;
  private status: OnlineClientStatus = "idle";
  private inputSequence = 0;
  private latest: unknown | null = null;
  private manuallyClosed = false;

  public constructor(private readonly options: OnlineClientOptions = {}) {}

  public async connect(profile: SavedProfile): Promise<OnlineConnectResult> {
    this.disconnectSilently();
    this.manuallyClosed = false;
    this.latest = null;
    this.inputSequence = 0;

    this.setStatus("connecting");

    const targets = await this.resolveServerTargets();
    let lastError: unknown = null;

    for (const target of targets) {
      if (this.manuallyClosed) {
        throw new Error("Connection closed");
      }

      try {
        const connection = await this.connectToTarget(target.wsUrl);

        this.connection = connection;
        this.setStatus("connected");

        connection.send({
          Join: {
            name: nameForJoin(profile.name),
            cosmetics: {
              ball_color: hexToRgb(profile.ballColor),
              name_color: hexToRgb(profile.nameColor)
            }
          }
        });

        return target;
      } catch (error) {
        lastError = error;
      }
    }

    this.connection = null;
    this.setStatus("error");

    const error = lastError instanceof Error ? lastError : new Error("WebSocket connection failed");

    this.options.onError?.(error);
    throw error;
  }

  public disconnect(): void {
    this.manuallyClosed = true;
    this.connection?.close();
    this.connection = null;
    this.latest = null;
    this.inputSequence = 0;
    this.setStatus("idle");
  }

  public disconnectSilently(): void {
    this.manuallyClosed = true;
    this.connection?.close();
    this.connection = null;
    this.latest = null;
    this.inputSequence = 0;
    this.status = "idle";
  }

  public sendInput(x: number, y: number): void {
    if (!this.connection || this.connection.getState() !== "open") {
      return;
    }

    this.inputSequence += 1;

    this.connection.send({
      Input: {
        direction: { x, y },
        sequence: this.inputSequence
      }
    });
  }

  public chooseUpgrade(upgrade: unknown): void {
    this.connection?.send({
      ChooseUpgrade: {
        upgrade
      }
    });
  }

  public respawn(): void {
    this.connection?.send("Respawn");
  }

  public ping(): void {
    this.connection?.send({
      Ping: {
        client_time_ms: Date.now()
      }
    });
  }

  public latestMessage(): unknown | null {
    return this.latest;
  }

  public getStatus(): OnlineClientStatus {
    return this.status;
  }

  public getConnectionState(): ConnectionState | null {
    return this.connection?.getState() ?? null;
  }

  private async connectToTarget(wsUrl: string): Promise<ServerConnection> {
    let suppressErrors = true;

    const connection = new ServerConnection(wsUrl, {
      reconnect: false,
      onStateChange: (state) => {
        if (!suppressErrors && this.connection === connection) {
          this.handleConnectionState(state);
        }
      },
      onMessage: (message) => {
        this.latest = message;
        this.options.onMessage?.(message);
      },
      onError: () => {
        if (!suppressErrors && !this.manuallyClosed && this.connection === connection) {
          this.setStatus("error");
          this.options.onError?.(new Error("WebSocket error"));
        }
      }
    });

    try {
      await withTimeout(
        connection.connect(),
        this.options.connectionTimeoutMs ?? DEFAULT_CONNECTION_TIMEOUT_MS,
        "WebSocket connection timeout"
      );
      suppressErrors = false;
      return connection;
    } catch (error) {
      suppressErrors = true;
      connection.close();
      throw error;
    }
  }

  private async resolveServerTargets(): Promise<OnlineConnectResult[]> {
    const targets = this.options.forceLocalServer
      ? createLocalTargets(this.options.localWsUrl)
      : createConfiguredTargets(this.options.targets, this.options.localWsUrl);

    if (targets.length === 0) {
      throw new Error("No WebSocket server configured");
    }

    if (targets.length === 1) {
      const target = targets[0];

      if (!target) {
        throw new Error("No WebSocket server configured");
      }

      return [
        {
          wsUrl: target.wsUrl,
          healthUrl: target.healthUrl,
          regionId: target.regionId,
          regionName: target.regionName
        }
      ];
    }

    return await measureAndSortTargets(targets, this.options.pingTimeoutMs ?? DEFAULT_PING_TIMEOUT_MS);
  }

  private handleConnectionState(state: ConnectionState): void {
    if (this.manuallyClosed) {
      return;
    }

    if (state === "open") {
      this.setStatus("connected");
      return;
    }

    if (state === "connecting") {
      this.setStatus("connecting");
      return;
    }

    if (state === "closed") {
      this.setStatus("closed");
      return;
    }

    if (state === "error") {
      this.setStatus("error");
    }
  }

  private setStatus(status: OnlineClientStatus): void {
    this.status = status;
    this.options.onStatusChange?.(status);
  }
}

export function createOnlineClient(
  callbacks: Pick<OnlineClientOptions, "onStatusChange" | "onMessage" | "onError"> = {}
): OnlineClient {
  return new OnlineClient({
    ...callbacks
  });
}

export function createLocalOnlineClient(
  callbacks: Pick<OnlineClientOptions, "onStatusChange" | "onMessage" | "onError"> = {}
): OnlineClient {
  return new OnlineClient({
    forceLocalServer: true,
    ...callbacks
  });
}

function createConfiguredTargets(
  explicitTargets: OnlineServerTarget[] | undefined,
  localWsUrl: string | undefined
): OnlineServerTarget[] {
  if (explicitTargets && explicitTargets.length > 0) {
    return uniqueTargets(explicitTargets.map(normalizeTarget).filter(isValidTarget));
  }

  const envTargets = createEnvTargets();

  if (envTargets.length > 0) {
    return uniqueTargets(envTargets);
  }

  return createLocalTargets(localWsUrl);
}

function createEnvTargets(): OnlineServerTarget[] {
  const targets: OnlineServerTarget[] = [];
  const singleUrl = readEnvString("VITE_ONLINE_WS_URL");

  if (singleUrl) {
    targets.push({
      wsUrl: singleUrl,
      healthUrl: wsUrlToHealthUrl(singleUrl),
      regionId: "default",
      regionName: "Default",
      priority: 0
    });
  }

  const euUrl = readEnvString("VITE_EU_WS_URL");

  if (euUrl) {
    targets.push({
      wsUrl: euUrl,
      healthUrl: wsUrlToHealthUrl(euUrl),
      regionId: "eu",
      regionName: "Europe",
      priority: 10
    });
  }

  const naUrl = readEnvString("VITE_NA_WS_URL");

  if (naUrl) {
    targets.push({
      wsUrl: naUrl,
      healthUrl: wsUrlToHealthUrl(naUrl),
      regionId: "na",
      regionName: "North America",
      priority: 20
    });
  }

  for (const target of parseTargetList(readEnvString("VITE_ONLINE_WS_URLS"))) {
    targets.push(target);
  }

  return uniqueTargets(targets.map(normalizeTarget).filter(isValidTarget));
}

function parseTargetList(value: string | null): OnlineServerTarget[] {
  if (!value) {
    return [];
  }

  return value
    .split(",")
    .map((part, index) => parseTargetEntry(part, index + 100))
    .filter((target): target is OnlineServerTarget => target !== null);
}

function parseTargetEntry(value: string, priority: number): OnlineServerTarget | null {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  const separatorIndex = trimmed.indexOf("=");

  if (separatorIndex === -1) {
    return {
      wsUrl: trimmed,
      healthUrl: wsUrlToHealthUrl(trimmed),
      priority
    };
  }

  const regionId = trimmed.slice(0, separatorIndex).trim();
  const wsUrl = trimmed.slice(separatorIndex + 1).trim();

  if (!regionId || !wsUrl) {
    return null;
  }

  return {
    wsUrl,
    healthUrl: wsUrlToHealthUrl(wsUrl),
    regionId,
    regionName: regionId.toUpperCase(),
    priority
  };
}

function createLocalTargets(explicitUrl?: string): OnlineServerTarget[] {
  return localWsUrls(explicitUrl).map((wsUrl, index) => ({
    wsUrl,
    healthUrl: wsUrlToHealthUrl(wsUrl),
    regionId: "local",
    regionName: "Local",
    priority: index
  }));
}

function normalizeTarget(target: OnlineServerTarget): OnlineServerTarget {
  return {
    ...target,
    wsUrl: target.wsUrl.trim(),
    healthUrl: target.healthUrl?.trim() || wsUrlToHealthUrl(target.wsUrl),
    priority: target.priority ?? 0
  };
}

function isValidTarget(target: OnlineServerTarget): boolean {
  return target.wsUrl.length > 0 && (target.wsUrl.startsWith("ws://") || target.wsUrl.startsWith("wss://"));
}

async function measureAndSortTargets(
  targets: OnlineServerTarget[],
  timeoutMs: number
): Promise<OnlineConnectResult[]> {
  const measured = await Promise.all(targets.map((target) => measureTarget(target, timeoutMs)));

  return measured
    .sort((a, b) => {
      if (a.reachable !== b.reachable) {
        return a.reachable ? -1 : 1;
      }

      if (a.healthMs !== b.healthMs) {
        return (a.healthMs ?? Number.MAX_SAFE_INTEGER) - (b.healthMs ?? Number.MAX_SAFE_INTEGER);
      }

      return a.priority - b.priority;
    })
    .map(({ priority: _priority, reachable: _reachable, ...target }) => target);
}

async function measureTarget(target: OnlineServerTarget, timeoutMs: number): Promise<MeasuredTarget> {
  const priority = target.priority ?? 0;
  const fallback: MeasuredTarget = {
    wsUrl: target.wsUrl,
    healthUrl: target.healthUrl,
    regionId: target.regionId,
    regionName: target.regionName,
    priority,
    reachable: false
  };

  if (!target.healthUrl) {
    return fallback;
  }

  const startedAt = performance.now();
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(withCacheBust(target.healthUrl), {
      cache: "no-store",
      signal: controller.signal
    });

    if (!response.ok) {
      return fallback;
    }

    return {
      ...fallback,
      healthMs: Math.round(performance.now() - startedAt),
      reachable: true
    };
  } catch {
    return fallback;
  } finally {
    window.clearTimeout(timeout);
  }
}

function localWsUrls(explicitUrl?: string): string[] {
  const urls: string[] = [];
  const envUrl = readEnvString("VITE_LOCAL_WS_URL");

  if (typeof explicitUrl === "string" && explicitUrl.trim().length > 0) {
    urls.push(explicitUrl.trim());
  }

  if (envUrl) {
    urls.push(envUrl);
  }

  for (const url of defaultLocalWsUrls()) {
    urls.push(url);
  }

  return unique(urls);
}

function defaultLocalWsUrls(): string[] {
  if (typeof window === "undefined") {
    return ["ws://127.0.0.1:8080/ws", "ws://localhost:8080/ws"];
  }

  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const hostname = window.location.hostname || "localhost";
  const urls: string[] = [];

  if (hostname === "localhost") {
    urls.push(`${protocol}//127.0.0.1:8080/ws`);
    urls.push(`${protocol}//localhost:8080/ws`);
    return urls;
  }

  if (hostname === "127.0.0.1") {
    urls.push(`${protocol}//127.0.0.1:8080/ws`);
    urls.push(`${protocol}//localhost:8080/ws`);
    return urls;
  }

  urls.push(`${protocol}//${hostname}:8080/ws`);
  urls.push(`${protocol}//127.0.0.1:8080/ws`);
  urls.push(`${protocol}//localhost:8080/ws`);

  return urls;
}

function wsUrlToHealthUrl(wsUrl: string): string | undefined {
  try {
    const url = new URL(wsUrl);
    url.protocol = url.protocol === "wss:" ? "https:" : "http:";
    url.pathname = url.pathname.replace(/\/ws\/?$/, "/health");

    if (!url.pathname.endsWith("/health")) {
      url.pathname = "/health";
    }

    url.search = "";
    url.hash = "";

    return url.toString();
  } catch {
    return undefined;
  }
}

function withCacheBust(url: string): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}t=${Date.now()}`;
}

function readEnvString(key: string): string | null {
  const value = import.meta.env[key];

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function uniqueTargets(targets: OnlineServerTarget[]): OnlineServerTarget[] {
  const seen = new Set<string>();
  const result: OnlineServerTarget[] = [];

  for (const target of targets) {
    if (seen.has(target.wsUrl)) {
      continue;
    }

    seen.add(target.wsUrl);
    result.push(target);
  }

  return result;
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter((value) => value.length > 0))];
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace("#", "");
  const value = Number.parseInt(normalized, 16);

  if (!Number.isFinite(value) || normalized.length !== 6) {
    return { r: 66, g: 135, b: 245 };
  }

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255
  };
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  let timeoutId = 0;

  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    window.clearTimeout(timeoutId);
  }
}